/**
 * Consumer Entity Hub Page
 *
 * Detail hub page for a borrowing entity, showing KPIs, linked cases,
 * ownership structure, documents, and activity timeline.
 * Uses EntityHubTemplate from design-system-ui for consistent layout.
 *
 * @see Issue #1199 - Consumer Entity Hub
 */

import EntityIcon from '@mui/icons-material/AccountBalance';
import CaseIcon from '@mui/icons-material/Gavel';
import OwnersIcon from '@mui/icons-material/Group';
import GuarantorIcon from '@mui/icons-material/Shield';
import {
  Box,
  Container,
  DataTable,
  type DataTableColumn,
  EntityActivityTimeline,
  EntityHubTemplate,
  GlassAlert,
  GlassCard,
  GlassChip,
  Grid,
  type KpiCard,
  PanelAttributeList,
  ShimmerLoader,
  Stack,
  snakeToTitle,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { type NavTab } from '@ops-matrix/shared-types';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { trpc } from '../../lib/trpc/client';
import {
  useCasesByEntity,
  useConsumerDocuments,
  useMyBorrowingEntity,
  useMyEntityOwnershipTree,
  useMyGuarantees,
} from '../../lib/trpc/hooks';
import { getEntityStatusColor } from '../../lib/utils/entity-helpers';

// ============================================================================
// Types
// ============================================================================

interface CaseBorrowerRow {
  id: string;
  caseId: string;
  caseNumber?: string | null;
  caseTitle?: string | null;
  role: string;
  status?: string | null;
  addedAt?: string | null;
}

interface OwnerRecord {
  id: string;
  ownerName: string | null;
  ownerType: string;
  ownershipPercentage: number;
  role: string;
  signingAuthority: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

const formatRole = snakeToTitle;

// ============================================================================
// Tab Definitions
// ============================================================================

const ENTITY_HUB_TABS: NavTab[] = [
  { id: 'overview', label: 'tabs.details', path: 'overview' },
  { id: 'applications', label: 'entityHub.tabs.applications', path: 'applications' },
  { id: 'loans', label: 'entityHub.tabs.loans', path: 'loans' },
  { id: 'ownership', label: 'entityHub.tabs.ownership', path: 'ownership' },
  { id: 'documents', label: 'entityHub.tabs.documents', path: 'documents' },
  { id: 'activity', label: 'entityHub.tabs.activity', path: 'activity' },
];

// ============================================================================
// Tab Components
// ============================================================================

function CasesTab({ entityId }: { entityId: string }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data, isPending } = useCasesByEntity(entityId);
  const cases = (data?.data ?? []) as CaseBorrowerRow[];

  const columns = useMemo(
    (): DataTableColumn<CaseBorrowerRow>[] => [
      {
        id: 'caseNumber',
        header: t('entityHub.loans.caseNumber'),
        accessor: 'caseNumber',
        width: 130,
        cell: (_value, row) => row.caseNumber || '-',
      },
      {
        id: 'caseTitle',
        header: t('entityHub.loans.title'),
        accessor: 'caseTitle',
        minWidth: 200,
        cell: (_value, row) => row.caseTitle || '-',
      },
      {
        id: 'role',
        header: t('entityHub.loans.role'),
        accessor: 'role',
        width: 160,
        cell: (_value, row) => {
          const roleKey = `entityHub.loans.role.${row.role}`;
          const translated = t(roleKey, formatRole(row.role));
          return <GlassChip label={translated} size="small" variant="outlined" />;
        },
      },
      {
        id: 'status',
        header: t('entityHub.loans.status'),
        accessor: 'status',
        width: 120,
        cell: (_value, row) => (
          <GlassChip label={row.status ?? '-'} size="small" color={row.status === 'active' ? 'success' : 'default'} />
        ),
      },
    ],
    [t],
  );

  if (isPending) {
    return <ShimmerLoader variant="rectangular" height={200} />;
  }

  if (cases.length === 0) {
    return (
      <GlassCard sx={{ p: 4, textAlign: 'center' }}>
        <CaseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="textSecondary">{t('consumer.entityHub.noCases')}</Typography>
        <Typography variant="body2" color="textDisabled" sx={{ mt: 0.5 }}>
          {t('entityHub.loans.emptyDescription')}
        </Typography>
      </GlassCard>
    );
  }

  return (
    <GlassCard>
      <DataTable
        data={cases}
        columns={columns}
        getRowId={(row) => row.id}
        density="compact"
        onRowClick={(row) => {
          if (row.caseId) {
            navigate(`/applications/${row.caseId}`);
          }
        }}
      />
    </GlassCard>
  );
}

function OwnershipTab({ entityId }: { entityId: string }) {
  const { t } = useI18n();
  const { data, isPending } = useMyEntityOwnershipTree(entityId);
  const owners = (data?.owners ?? []) as OwnerRecord[];

  if (isPending) {
    return <ShimmerLoader variant="rectangular" height={200} />;
  }

  if (owners.length === 0) {
    return (
      <GlassCard sx={{ p: 4, textAlign: 'center' }}>
        <OwnersIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="textSecondary">{t('entityHub.ownership.empty')}</Typography>
        <Typography variant="body2" color="textDisabled" sx={{ mt: 0.5 }}>
          {t('entityHub.ownership.emptyDescription')}
        </Typography>
      </GlassCard>
    );
  }

  const totalOwnership = owners.reduce((sum, o) => sum + o.ownershipPercentage, 0);

  return (
    <Box>
      {totalOwnership > 100 && (
        <GlassAlert severity="warning" visible sx={{ mb: 2 }}>
          {t('entityHub.ownership.ownershipWarning')}
        </GlassAlert>
      )}
      <Grid container spacing={2}>
        {owners.map((owner) => (
          <Grid key={owner.id} size={{ xs: 12, sm: 6, md: 4 }}>
            <GlassCard sx={{ p: 2, height: '100%' }}>
              <Stack spacing={1}>
                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600 }}>
                    {owner.ownerName || t('entityHub.ownership.unknownOwner')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {owner.ownershipPercentage}%
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                  <GlassChip label={formatRole(owner.role)} size="small" variant="outlined" />
                  <GlassChip
                    label={t(`entityHub.ownership.${owner.ownerType === 'individual' ? 'individual' : 'entity'}`)}
                    size="small"
                    color="info"
                  />
                  {owner.signingAuthority && (
                    <GlassChip label={t('entityHub.ownership.signingAuth')} size="small" color="success" />
                  )}
                </Stack>
              </Stack>
            </GlassCard>
          </Grid>
        ))}
      </Grid>
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Typography variant="body2" color="textSecondary">
          {t('entityHub.ownership.totalOwnership')}: {totalOwnership}%
        </Typography>
      </Box>
    </Box>
  );
}

function DocumentsTab() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { data, isPending } = useConsumerDocuments({ page: 1, limit: 50 });
  const documents = data?.data ?? [];

  if (isPending) {
    return <ShimmerLoader variant="rectangular" height={200} />;
  }

  if (documents.length === 0) {
    return (
      <GlassCard sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="textSecondary">{t('entityHub.documents.empty')}</Typography>
        <Typography variant="body2" color="textDisabled" sx={{ mt: 0.5 }}>
          {t('entityHub.documents.emptyDescription')}
        </Typography>
      </GlassCard>
    );
  }

  const docColumns: DataTableColumn<(typeof documents)[0]>[] = [
    {
      id: 'name',
      header: t('entityHub.documents.name'),
      accessor: 'name' as keyof (typeof documents)[0],
      minWidth: 200,
    },
    {
      id: 'type',
      header: t('entityHub.documents.type'),
      accessor: 'type' as keyof (typeof documents)[0],
      width: 140,
    },
    {
      id: 'status',
      header: t('entityHub.documents.status'),
      accessor: 'status' as keyof (typeof documents)[0],
      width: 120,
      cell: (value) => <GlassChip label={String(value ?? '-')} size="small" />,
    },
  ];

  return (
    <GlassCard>
      <DataTable
        data={documents}
        columns={docColumns}
        getRowId={(row) => (row as { id: string }).id}
        density="compact"
        hoverable
        onRowClick={(row) => {
          const id = (row as { id: string }).id;
          if (id) navigate(`/documents/${id}/view`);
        }}
      />
    </GlassCard>
  );
}

function ActivityTab({ entityId }: { entityId: string }) {
  const { t } = useI18n();
  const { data, isPending } = trpc.activity.byEntity.useQuery(
    { entityType: 'borrowing_entity', entityId, page: 1, limit: 20 },
    { enabled: !!entityId },
  );
  const events = data?.data ?? [];

  if (isPending) {
    return <ShimmerLoader variant="rectangular" height={300} />;
  }

  return (
    <GlassCard>
      <EntityActivityTimeline
        title={t('entityHub.activity.title')}
        events={events}
        loading={isPending}
        maxItems={50}
        groupByTimeOfDay
        showThreading
        showAvatars
        relativeTime
        emptyMessage={t('entityHub.activity.empty')}
      />
    </GlassCard>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function EntityHubPage() {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();

  // State-based tabs (React Router v6 — no useUrlTabs)
  const [activeTab, setActiveTab] = useState('overview');
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  // Data fetching — entity always loads; tab data lazy-loads when tab is active
  const { data: entityData, isPending: entityLoading, error } = useMyBorrowingEntity(entityId ?? null);
  const casesEnabled = activeTab === 'loans' || activeTab === 'overview';
  const ownershipEnabled = activeTab === 'ownership' || activeTab === 'overview';
  const guaranteesEnabled = activeTab === 'overview';
  const { data: casesData } = useCasesByEntity(casesEnabled ? (entityId ?? null) : null);
  const { data: ownershipData } = useMyEntityOwnershipTree(ownershipEnabled ? (entityId ?? null) : null);
  const { data: guaranteesData } = useMyGuarantees(
    guaranteesEnabled && entityId ? { borrowingEntityId: entityId } : undefined,
  );

  const entity = entityData as
    | {
        id: string;
        legalName: string;
        entityType: string;
        ein?: string;
        status?: string;
        ownerCount?: number;
      }
    | undefined;

  const caseBorrowers = (casesData?.data ?? []) as CaseBorrowerRow[];
  const guarantees = (guaranteesData?.data ?? []) as Array<{ id: string; status: string }>;

  // KPI card definitions
  const kpiCards: KpiCard[] = useMemo((): KpiCard[] => {
    const activeCases = caseBorrowers.length;
    const activeGuarantees = guarantees.filter((g) => g.status === 'ACTIVE' || g.status === 'active').length;
    const ownerCount = entity?.ownerCount ?? (ownershipData?.owners ?? []).length;

    return [
      {
        title: t('consumer.entityHub.kpiActiveCases'),
        value: activeCases,
        color: 'primary',
        icon: <CaseIcon />,
      },
      {
        title: t('consumer.entityHub.kpiActiveGuarantees'),
        value: activeGuarantees,
        subtitle: `${guarantees.length} ${t('entityHub.kpi.totalGuarantees').toLowerCase()}`,
        color: 'warning',
        icon: <GuarantorIcon />,
      },
      {
        title: t('entityHub.kpi.ownerCount'),
        value: ownerCount,
        color: 'info',
        icon: <OwnersIcon />,
      },
    ];
  }, [caseBorrowers.length, guarantees, entity?.ownerCount, ownershipData?.owners, t]);

  // Build page description
  const pageDescription = useMemo(() => {
    if (!entity) return t('consumer.entityHub.description');
    const parts: string[] = [t('consumer.entityHub.description')];
    if (entity.ein) parts.push(`EIN: ${entity.ein}`);
    return parts.join(' \u2022 ');
  }, [entity, t]);

  // Build badges
  const pageBadges = useMemo(() => {
    if (!entity) return undefined;
    return (
      <>
        {entity.entityType && <GlassChip size="small" label={entity.entityType.toUpperCase()} variant="outlined" />}
        {entity.status && <GlassChip size="small" label={entity.status} color={getEntityStatusColor(entity.status)} />}
      </>
    );
  }, [entity]);

  // Loading state
  if (entityLoading) {
    return (
      <PageTemplate variant="detail" icon={EntityIcon} onBack={() => navigate('/entities')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <ShimmerLoader variant="circular" width={40} height={40} />
        </Box>
      </PageTemplate>
    );
  }

  // Error state
  if (error || !entity) {
    return (
      <PageTemplate variant="detail" icon={EntityIcon} onBack={() => navigate('/entities')}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <GlassAlert severity="error" visible sx={{ mb: 3 }}>
            {error?.message ?? t('common.notFound')}
          </GlassAlert>
        </Container>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      variant="detail"
      title={entity.legalName ?? t('consumer.entityHub.title')}
      description={pageDescription}
      icon={EntityIcon}
      onBack={() => navigate('/entities')}
      badges={pageBadges}
    >
      <EntityHubTemplate
        kpiCards={kpiCards}
        tabs={ENTITY_HUB_TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        loading={entityLoading}
      >
        {activeTab === 'overview' && entityData && (
          <GlassCard>
            <PanelAttributeList
              attributes={[
                { label: t('borrowingEntities.legalName'), value: entityData.legalName },
                { label: t('borrowingEntities.entityType'), value: entityData.entityType?.toUpperCase() },
                { label: t('borrowingEntities.ein'), value: entityData.ein || '-' },
                { label: t('borrowingEntities.stateOfFormation'), value: entityData.stateOfFormation || '-' },
                { label: t('borrowingEntities.status'), value: entityData.status },
              ]}
              dividers
            />
          </GlassCard>
        )}
        {activeTab === 'applications' && (
          <GlassCard sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary">{t('entityHub.applications.emptyDescription')}</Typography>
          </GlassCard>
        )}
        {activeTab === 'loans' && <CasesTab entityId={entityId!} />}
        {activeTab === 'ownership' && <OwnershipTab entityId={entityId!} />}
        {activeTab === 'documents' && <DocumentsTab />}
        {activeTab === 'activity' && <ActivityTab entityId={entityId!} />}
      </EntityHubTemplate>
    </PageTemplate>
  );
}
